import type { WorkflowEvent } from '@/shared/hooks/api/workflows'

export type TypeCStandardCheckStepId =
  | 'get_applicability_context'
  | 'discover_integrated_components'
  | 'retrieve_type_c_standards'
  | 'llm_applicability'
  | 'persist_applicability'

export type TypeCStandardCheckTaskStatus =
  | 'pending'
  | 'active'
  | 'done'
  | 'skipped'

export type TypeCStandardCheckLlmTrackState =
  | { phase: 'idle' }
  | {
      phase: 'running'
      batchIndex: number
      batchTotal: number
    }
  | { phase: 'done' }

export type TypeCStandardCheckTaskState = {
  steps: Record<TypeCStandardCheckStepId, TypeCStandardCheckTaskStatus>
  llmTypeC: TypeCStandardCheckLlmTrackState
  retrieveTypeCCandidateCount: number | null
}

export const TYPE_C_STANDARD_CHECK_WORKFLOW_TYPE =
  'type_c_standard_applicability_check_rerun'

const TYPE_C_STANDARD_CHECK_ORDERED_STEPS: TypeCStandardCheckStepId[] = [
  'get_applicability_context',
  'discover_integrated_components',
  'retrieve_type_c_standards',
  'llm_applicability',
  'persist_applicability',
]

function emptyTypeCStandardCheckSteps(): Record<
  TypeCStandardCheckStepId,
  TypeCStandardCheckTaskStatus
> {
  return {
    get_applicability_context: 'pending',
    discover_integrated_components: 'pending',
    retrieve_type_c_standards: 'pending',
    llm_applicability: 'pending',
    persist_applicability: 'pending',
  }
}

export function getInitialTypeCStandardCheckTaskState(): TypeCStandardCheckTaskState {
  return {
    steps: emptyTypeCStandardCheckSteps(),
    llmTypeC: { phase: 'idle' },
    retrieveTypeCCandidateCount: null,
  }
}

function cloneTypeCStandardCheckState(
  state: TypeCStandardCheckTaskState
): TypeCStandardCheckTaskState {
  return {
    steps: { ...state.steps },
    llmTypeC: state.llmTypeC,
    retrieveTypeCCandidateCount: state.retrieveTypeCCandidateCount,
  }
}

function indexOfTypeCStandardCheckStep(stepId: TypeCStandardCheckStepId): number {
  return TYPE_C_STANDARD_CHECK_ORDERED_STEPS.indexOf(stepId)
}

function applyTypeCStandardCheckStepStarted(
  next: TypeCStandardCheckTaskState,
  stepId: TypeCStandardCheckStepId
): void {
  const targetIndex = indexOfTypeCStandardCheckStep(stepId)
  for (let index = 0; index < targetIndex; index++) {
    const id = TYPE_C_STANDARD_CHECK_ORDERED_STEPS[index]!
    const status = next.steps[id]
    if (status === 'pending') {
      if (id === 'discover_integrated_components') {
        next.steps[id] = 'skipped'
      } else {
        next.steps[id] = 'done'
      }
    }
  }

  if (
    stepId === 'retrieve_type_c_standards' &&
    next.steps.discover_integrated_components === 'pending'
  ) {
    next.steps.discover_integrated_components = 'skipped'
  }

  next.steps[stepId] = 'active'
}

function applyTypeCStandardCheckLlmStreamEvent(
  next: TypeCStandardCheckTaskState,
  kind: 'started' | 'completed',
  batch?: { index: number; total: number }
): void {
  if (kind === 'started' && batch != null) {
    next.llmTypeC = {
      phase: 'running',
      batchIndex: batch.index,
      batchTotal: batch.total,
    }
    return
  }
  if (kind === 'completed' && batch != null) {
    if (batch.index === batch.total) {
      next.llmTypeC = { phase: 'done' }
    } else {
      next.llmTypeC = {
        phase: 'running',
        batchIndex: batch.index,
        batchTotal: batch.total,
      }
    }
  }
}

export function reduceTypeCStandardCheckTaskState(
  state: TypeCStandardCheckTaskState,
  event: WorkflowEvent
): TypeCStandardCheckTaskState {
  const next = cloneTypeCStandardCheckState(state)

  if (event.type === 'workflow.started') {
    const workflowType =
      'workflowType' in event &&
      typeof (event as { workflowType?: string }).workflowType === 'string'
        ? (event as { workflowType: string }).workflowType
        : undefined
    if (workflowType === TYPE_C_STANDARD_CHECK_WORKFLOW_TYPE) {
      return getInitialTypeCStandardCheckTaskState()
    }
    return state
  }

  if (event.type === 'workflow.completed') {
    for (const id of TYPE_C_STANDARD_CHECK_ORDERED_STEPS) {
      next.steps[id] = 'done'
    }
    if (next.llmTypeC.phase === 'running') {
      next.llmTypeC = { phase: 'done' }
    }
    return next
  }

  if (event.type === 'step.started') {
    const step = (event as { step?: string }).step
    if (
      step != null &&
      TYPE_C_STANDARD_CHECK_ORDERED_STEPS.includes(step as TypeCStandardCheckStepId)
    ) {
      applyTypeCStandardCheckStepStarted(next, step as TypeCStandardCheckStepId)
    }
    return next
  }

  if (event.type === 'step.completed') {
    const step = (event as { step?: string }).step
    const result = (event as { result?: unknown }).result
    if (
      step != null &&
      TYPE_C_STANDARD_CHECK_ORDERED_STEPS.includes(step as TypeCStandardCheckStepId)
    ) {
      if (
        result !== undefined &&
        result !== null &&
        typeof result === 'object' &&
        'candidatesCount' in result
      ) {
        const rawCount = (result as { candidatesCount: unknown }).candidatesCount
        const count =
          typeof rawCount === 'number' ? rawCount : Number(rawCount)
        if (!Number.isNaN(count) && step === 'retrieve_type_c_standards') {
          next.retrieveTypeCCandidateCount = count
        }
      }
      next.steps[step as TypeCStandardCheckStepId] = 'done'
      if (step === 'llm_applicability' && next.llmTypeC.phase === 'running') {
        next.llmTypeC = { phase: 'done' }
      }
    }
    return next
  }

  if (event.type === 'llm.started') {
    const step = (event as { step?: string }).step
    const stepId = (event as { stepId?: string }).stepId
    const batch = (event as { batch?: { index: number; total: number } }).batch
    if (step === 'llm_applicability' && stepId === 'type_c' && batch != null) {
      applyTypeCStandardCheckLlmStreamEvent(next, 'started', batch)
    }
    return next
  }

  if (event.type === 'llm.completed') {
    const step = (event as { step?: string }).step
    const stepId = (event as { stepId?: string }).stepId
    const batch = (event as { batch?: { index: number; total: number } }).batch
    if (step === 'llm_applicability' && stepId === 'type_c' && batch != null) {
      applyTypeCStandardCheckLlmStreamEvent(next, 'completed', batch)
    }
    return next
  }

  return state
}
